import React, { useEffect, useState, useRef, useMemo } from "react";
import { connect } from "react-redux";
import API from "../../../utils/API";
import MesTable from "../../../components/Table/MesTable";
import stopFilled from "../../../assets/icons/message-manager/stopFilled.svg";
import constants from "../../../utils/constants";
import commonMethods from "../../../utils/common-methods";
import { FormattedMessage } from "react-intl";
import useInfiniteScroll from "../../../components/common/useInfiniteScroll";
import { trackEvent } from "../../../utils/analytics";

const MESSENGER_TEXT_PREFIX = "MESSENGER_";

function MessageStatusTable(props) {
  const { updateFlag, setUpdateFlag, onLoadStatusTable } = props;
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [sortByCol, setSortByCol] = useState("sent");
  const [sortByAsc, setSortByAsc] = useState(false);
  const [startIndexForTable, setStartIndexForTable] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [statusData, setStatusData] = useState([]);

  const tableRef = useRef();
  const tableWrapperRef = useRef();
  const modalRef = useRef(null);
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    modalRef.current = new window.bootstrap.Modal(
      document.getElementById("notificationModal"),
    );
  }, []);

  const loadStatusTable = async (map, resetSelection = true) => {
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }

    try {
      const res = await API.getqueuedMessages();
      res.data.forEach((mess) => {
        map.data[mess.id] = {
          id: mess.id,
          message: mess.title,
          type: mess.format,
          sent: mess.scheduler.timeStart
            ? commonMethods.formatDate(mess.scheduler.timeStart)
            : null,
          groups: mess.groups.map((role) => role.name).join(", "),
          status: mess.status,
          checked: false,
        };
      });
      map.filteredIds = Object.keys(map.data);
      map.ids = Object.keys(map.data);
      setStatusData(res.data);
      setDataCount(map.ids.length);
      setTotalDataCount(map.ids.length);
      setDataLoaded(true);

      // Track dashboard view once on initial load
      if (!hasTrackedRef.current && res.data.length > 0) {
        hasTrackedRef.current = true;
        const messageTypes = {};
        const uniqueGroups = new Set();

        res.data.forEach((mess) => {
          // Count message types
          const type = mess.format || "unknown";
          messageTypes[type] = (messageTypes[type] || 0) + 1;

          // Collect unique groups
          mess.groups?.forEach((group) => uniqueGroups.add(group.id));
        });

        trackEvent(
          "message_manager.dashboard_status",
          {
            running_message_count: res.data.length,
            message_type_ticker_count: messageTypes["ticker"] || 0,
            message_type_full_screen_count: messageTypes["full-screen"] || 0,
            message_type_half_screen_count: messageTypes["half-screen"] || 0,
            has_ticker_messages: (messageTypes["ticker"] || 0) > 0,
            has_full_screen_messages: (messageTypes["full-screen"] || 0) > 0,
            has_half_screen_messages: (messageTypes["half-screen"] || 0) > 0,
            unique_groups_count: uniqueGroups.size,
          },
          props.user,
        );
      }
    } catch (err) {
      // If it's error 502, skip error handling
      if (err.response && err.response.status === 502) return;
      setError(err.message);
      setDataLoaded(true);
    }
  };

  useEffect(() => {
    if (onLoadStatusTable) {
      onLoadStatusTable(() => loadStatusTable(dataMap));
    }
  }, [onLoadStatusTable]);

  const dataMap = useMemo(() => {
    const map = {
      data: {},
      ids: [],
      selectedIds: {},
      filteredIds: [],
      sortedIds: [],
    };
    loadStatusTable(map);
    return map;
  }, []);

  // Hook for infinite scrolling
  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndexForTable,
    startIndexForTable,
    pageSize: 100,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
    rowHeight: 48,
  });

  // Hook for sorting data when data changes or sort column is changed
  useEffect(() => {
    if (dataLoaded) {
      if (searchValue !== "") {
        tableRef.current.search(dataMap);
      } else {
        sortMap(dataMap);
        setData(generateDataSlice(dataMap));
      }
    }
  }, [sortByCol, sortByAsc, dataLoaded, dataMap, statusData]);

  useEffect(() => {
    loadStatusTable(dataMap);
  }, [updateFlag]);

  const handleSingleDeletess = async (e, id) => {
    e.preventDefault();

    try {
      await API.stopMessages(id);
      setSuccess("Messages deleted successfully");
      modalRef.current.hide();
      setUpdateFlag((prev) => !prev);
    } catch (error) {
      setError("Error deleting messages");
      modalRef.current.hide();
    }
  };

  const tableColumnArray = [
    {
      Header: "MESSAGE",
      id: "message",
      accessor: "message",
      isVisible: true,
    },
    {
      Header: "TYPE",
      id: "type",
      accessor: "type",
      isVisible: true,
    },
    {
      Header: "SENT",
      id: "sent",
      accessor: "sent",
      isVisible: true,
    },
    {
      Header: "GROUPS",
      id: "groups",
      isVisible: true,
      accessor: "groups",
    },
    {
      Header: "STATUS",
      id: "status",
      isVisible: true,
      Cell: ({ value }) => (
        <div>
          <FormattedMessage id={MESSENGER_TEXT_PREFIX + value} />
        </div>
      ),
      accessor: "status",
    },
    {
      Header: "",
      id: "actions",
      width: 60,
      isVisible: true,
      Cell: ({ row }) => (
        <div className="actions">
          <button
            className="btn"
            id="stopMessage"
            onClick={(e) => {
              e.stopPropagation(); // Prevent click from propagating to the parent cell
              handleSingleDeletess(e, row.original.id);
            }}
          >
            <img
              src={stopFilled}
              className="deleteAction"
              alt={<FormattedMessage id="DELETE" />}
            />
          </button>
        </div>
      ),
    },
  ];

  const tableProps = {
    // The tableKey is for task#20542.
    // Each table requires a unique key to prevent preferences from being overwritten.
    tableKey: "sbmm_status_table",
    pageType: constants.pageType.MessageStatus,
    modalId: "Status",
    columns: tableColumnArray,
    data,
    setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount,
    setDataCount,
    totalDataCount,
    setSortBy,
    sortByCol,
    sortByAsc,
    success,
    setSuccess,
    error,
    setError,
    startIndex: startIndexForTable,
    selectedIds,
    setSelectedIds,
    searchValue,
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    initialContainerHeight: "288px",
  };

  return (
    <div>
      <MesTable {...tableProps} />
    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(MessageStatusTable);
